'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m } from 'framer-motion'
import { Instagram, LayoutGrid, PlayCircle, ShoppingCart } from 'lucide-react'
import { EASE } from '@/lib/motion'
import { ExplainerFrame } from './explainer-frame'

// One shopper's path, credited three ways. The bars are the real split
// each model produces for this journey, so the graphic is also the
// arithmetic: last-touch gives UGC discovery nothing, linear gives it a
// quarter, first-touch gives it everything.

const TOUCHES = [
  { icon: Instagram,   label: 'Instagram reel',   when: 'Day 1' },
  { icon: LayoutGrid,  label: 'Gallery click',    when: 'Day 3' },
  { icon: PlayCircle,  label: 'PDP video watched', when: 'Day 3' },
  { icon: ShoppingCart, label: 'Checkout',        when: 'Day 5' },
] as const

const MODELS = [
  { key: 'first',  label: 'First touch', split: [100, 0, 0, 0] },
  { key: 'last',   label: 'Last touch',  split: [0, 0, 0, 100] },
  { key: 'linear', label: 'Linear',      split: [25, 25, 25, 25] },
] as const

const ORDER_VALUE = 118

export function AttributionExplainer({ className }: { className?: string }) {
  return (
    <ExplainerFrame
      eyebrow="Attribution"
      title="One order, three ways to credit it"
      caption="Idukki keeps the whole path, so you choose the model in the report instead of inheriting whichever one your analytics tool defaults to."
      className={className}
    >
      {({ play, reduce, d }) => (
        <LazyMotion features={domAnimation}>
          <ol className="grid grid-cols-4 gap-2">
            {TOUCHES.map((t, i) => {
              const Icon = t.icon
              return (
                <m.li
                  key={t.label}
                  className="rounded-xl border bg-secondary/30 p-2.5 text-center"
                  initial={reduce ? false : { opacity: 0, y: 10 }}
                  animate={play ? { opacity: 1, y: 0 } : undefined}
                  transition={{ duration: 0.35, ease: EASE, delay: d(0.15 + i * 0.22) }}
                >
                  <Icon className="mx-auto h-4 w-4 text-primary" aria-hidden />
                  <p className="mt-1.5 text-[11px] font-semibold leading-tight text-foreground">{t.label}</p>
                  <p className="text-[10px] text-muted-foreground">{t.when}</p>
                </m.li>
              )
            })}
          </ol>

          <div className="mt-5 space-y-3">
            {MODELS.map((mod, mi) => (
              <div key={mod.key} className="grid grid-cols-[92px_1fr] items-center gap-3">
                <p className="text-xs font-medium text-foreground">{mod.label}</p>
                <div className="grid grid-cols-4 gap-1">
                  {mod.split.map((pct, ti) => (
                    <div key={ti} className="relative h-6 overflow-hidden rounded-md bg-secondary/50">
                      {pct > 0 && (
                        <m.div
                          className="absolute inset-y-0 left-0 origin-left rounded-md bg-primary"
                          style={{ width: '100%' }}
                          initial={reduce ? false : { scaleX: 0 }}
                          animate={play ? { scaleX: 1 } : undefined}
                          transition={{ duration: 0.45, ease: EASE, delay: d(1.1 + mi * 0.35 + ti * 0.06) }}
                        />
                      )}
                      <m.span
                        className={`absolute inset-0 flex items-center justify-center text-[10px] font-semibold tabular-nums ${pct > 0 ? 'text-primary-foreground' : 'text-muted-foreground'}`}
                        initial={reduce ? false : { opacity: 0 }}
                        animate={play ? { opacity: 1 } : undefined}
                        transition={{ duration: 0.3, delay: d(1.3 + mi * 0.35) }}
                      >
                        {pct > 0 ? `$${((ORDER_VALUE * pct) / 100).toFixed(0)}` : '0'}
                      </m.span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">Order value ${ORDER_VALUE}. Credit shown per touchpoint under each model.</p>
        </LazyMotion>
      )}
    </ExplainerFrame>
  )
}
